import { cn } from "@/lib/utils";
import { HomeReveal } from "@/components/home/HomeReveal";

const TONE_CLASS = {
  white: "bg-white",
  muted: "bg-matpro-section",
  dark: "bg-matpro-anthracite text-white",
};

/**
 * Homepage section shell — alternating tones, hairline divider, reveal.
 * Vertical rhythm comes from the divider margins (60–80px), Stripe-style.
 */
export function HomeSection({
  tone = "white",
  divider = true,
  reveal = true,
  className = "",
  contentClassName = "",
  testId,
  id,
  children,
}) {
  const section = (
    <section
      id={id}
      data-testid={testId}
      className={cn(TONE_CLASS[tone] || TONE_CLASS.white, className)}
    >
      {divider ? (
        <div className="matpro-container" aria-hidden>
          <div
            className={cn(
              "h-px w-full my-16 sm:my-20",
              tone === "dark" ? "bg-white/10" : "bg-matpro-hairline",
            )}
          />
        </div>
      ) : (
        <div className="h-12 sm:h-16" aria-hidden />
      )}

      <div className={cn("matpro-container pb-4 sm:pb-6", contentClassName)}>
        {children}
      </div>
    </section>
  );

  return reveal ? <HomeReveal>{section}</HomeReveal> : section;
}

/** Title + subtitle block shown at the start of each home section. */
export function HomeSectionHeader({
  kicker,
  title,
  subtitle,
  align = "center",
  action = null,
  titleId,
  dark = false,
  className = "",
}) {
  const isCenter = align === "center";

  return (
    <div
      className={cn(
        isCenter
          ? "mx-auto mb-10 max-w-3xl text-center sm:mb-12"
          : "mb-10 flex flex-col items-center gap-3 text-center sm:mb-12 sm:flex-row sm:items-end sm:justify-between sm:text-left",
        className,
      )}
    >
      <div className={isCenter ? undefined : "min-w-0"}>
        {kicker ? (
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-matpro-green">
            {kicker}
          </p>
        ) : null}
        <h2
          id={titleId}
          className={cn(
            "font-heading text-2xl font-extrabold tracking-tight sm:text-3xl",
            kicker ? "mt-2" : null,
            dark ? "text-white" : "text-matpro-anthracite",
          )}
        >
          {title}
        </h2>
        {subtitle ? (
          <p
            className={cn(
              "mt-2 text-sm leading-relaxed sm:text-base",
              isCenter ? "mx-auto max-w-xl" : "mx-auto max-w-xl sm:mx-0 sm:max-w-2xl",
              dark ? "text-zinc-400" : "text-matpro-anthracite-muted",
            )}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
